import searchIndex from "@/assets/data/search_index.json";

/**
 * Webpack-resolved URLs for bundled media (hashed filenames in production).
 * require.context only exists in the webpack runtime; under Jest it throws,
 * which is caught so the module still loads.
 */
let assetContext = null;
try {
  assetContext = require.context("@/assets/videos", true, /\.mp4$/);
} catch (e) {
  assetContext = null;
}

/**
 * True for external URLs (e.g. YouTube links).
 */
export function isExternal(path) {
  return /^https?:\/\//i.test(String(path || ""));
}

/**
 * True for video results (local mp4 files and bundled "asset:" paths).
 */
export function isVideo(path) {
  const raw = String(path || "");
  return raw.startsWith("asset:") || /\.(mp4|webm|mov|ogv)$/i.test(raw);
}

/**
 * Badge text for a result: "LINK" for external URLs, otherwise the extension.
 */
export function resultType(path) {
  if (isExternal(path)) return "LINK";
  return fileType(path);
}

/**
 * Resolve an index path to a usable href. "asset:" paths are looked up in the
 * webpack asset context; everything else is returned unchanged.
 */
export function resolvePath(path) {
  const raw = String(path || "");
  if (!raw.startsWith("asset:")) return raw;
  if (!assetContext) return raw;
  const mod = assetContext(`./${raw.slice(6)}`);
  return mod.default || mod;
}

/**
 * Lowercase + strip diacritics so accented characters (LT/PL/CZ/…) match
 * their plain-letter equivalents.
 */
export function normalizeText(value) {
  return String(value == null ? "" : value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();
}

/**
 * File name without directory/extension, e.g. "NFPA-FireAlarmBasicsFactSheet".
 */
export function fileName(path) {
  const base = String(path || "").split("/").pop() || "";
  return base.replace(/\.[^.]+$/, "");
}

/**
 * File extension in upper case for the type badge, e.g. "PDF" / "DOCX".
 */
export function fileType(path) {
  const match = /\.([^.]+)$/.exec(String(path || ""));
  return match ? match[1].toUpperCase() : "";
}

/**
 * Search the document index.
 *
 * @param {string} query raw user input
 * @param {Array}  docs  index entries ({ path, title, description })
 * @param {(key:string)=>string} resolve lang-key resolver (e.g. toLocal)
 * @returns {Array} entries that match, in index order (empty query -> [])
 */
export function searchDocuments(query, docs, resolve) {
  const needle = normalizeText(query);
  if (!needle) return [];

  return (docs || searchIndex).filter((doc) => {
    const name = isExternal(doc.path) ? "" : fileName(doc.path);
    const haystack = normalizeText(
      [resolve(doc.title), resolve(doc.description), name].join(" ")
    );
    return haystack.indexOf(needle) !== -1;
  });
}
